import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Zap,
  ArrowRight,
  ArrowLeft,
  Mail,
  RefreshCw,
  CheckCircle,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import api from "../lib/api";

const FP_EMAIL = "fp_email";
const FP_OTP = "fp_otp";
const FP_NEWPWD = "fp_newpwd";
const FP_DONE = "fp_done";

const PWD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  {
    id: "upper",
    label: "One uppercase letter (A-Z)",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: "lower",
    label: "One lowercase letter (a-z)",
    test: (p) => /[a-z]/.test(p),
  },
  { id: "num", label: "One number (0-9)", test: (p) => /[0-9]/.test(p) },
];

function PwdStrengthBar({ password }) {
  if (!password) return null;
  const passed = PWD_RULES.filter((r) => r.test(password)).length;
  const pct = (passed / PWD_RULES.length) * 100;
  const color =
    pct <= 25
      ? "bg-red-500"
      : pct <= 50
        ? "bg-orange-500"
        : pct <= 75
          ? "bg-yellow-500"
          : "bg-green-500";
  const label =
    pct <= 25 ? "Weak" : pct <= 50 ? "Fair" : pct <= 75 ? "Good" : "Strong";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${color}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={`text-xs font-medium ${
            pct <= 25
              ? "text-red-500"
              : pct <= 50
                ? "text-orange-500"
                : pct <= 75
                  ? "text-yellow-500"
                  : "text-green-500"
          }`}
        >
          {label}
        </span>
      </div>
      <ul className="space-y-1">
        {PWD_RULES.map((rule) => {
          const ok = rule.test(password);
          return (
            <li
              key={rule.id}
              className={`flex items-center gap-1.5 text-xs ${ok ? "text-green-500" : "text-muted-foreground"}`}
            >
              <span className="font-bold">{ok ? "✓" : "✗"}</span> {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ForgotPassword({ onBack }) {
  const [fpStep, setFpStep] = useState(FP_EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showCPwd, setShowCPwd] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const isPasswordValid = PWD_RULES.every((r) => r.test(newPwd));

  const startTimer = () => {
    setResendTimer(60);
    const id = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(id);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If this email is registered, a reset code has been sent.");
      setFpStep(FP_OTP);
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("New reset code sent!");
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/verify-reset-otp", { email, otp });
      toast.success("Code verified! Set your new password.");
      setFpStep(FP_NEWPWD);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements.");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email,
        otp,
        new_password: newPwd,
      });
      toast.success("Password reset successfully! Please sign in.");
      setFpStep(FP_DONE);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card border-border shadow-xl">
      <CardHeader className="space-y-1 pb-6">
        <div className="flex items-center gap-2">
          {fpStep !== FP_DONE && (
            <button
              onClick={
                fpStep === FP_EMAIL
                  ? onBack
                  : () => {
                      if (fpStep === FP_OTP) setFpStep(FP_EMAIL);
                      if (fpStep === FP_NEWPWD) setFpStep(FP_OTP);
                    }
              }
              className="text-muted-foreground hover:text-foreground transition-colors mr-1"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <CardTitle className="text-2xl font-heading uppercase tracking-wide">
            {fpStep === FP_EMAIL && "Forgot Password"}
            {fpStep === FP_OTP && "Check Your Email"}
            {fpStep === FP_NEWPWD && "New Password"}
            {fpStep === FP_DONE && "All Done!"}
          </CardTitle>
        </div>
        <CardDescription>
          {fpStep === FP_EMAIL &&
            "Enter your account email and we'll send a reset code."}
          {fpStep === FP_OTP && (
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" /> We sent a 6-digit code to{" "}
              <strong className="text-foreground ml-1">{email}</strong>
            </span>
          )}
          {fpStep === FP_NEWPWD &&
            "Choose a strong new password for your account."}
          {fpStep === FP_DONE && "Your password has been updated successfully."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {fpStep === FP_EMAIL && (
          <form onSubmit={handleRequestOTP} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Email Address
              </Label>
              <Input
                type="email"
                placeholder="you@gym.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={loading}
                className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                data-testid="fp-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                  Sending code…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Send Reset Code <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
        )}

        {fpStep === FP_OTP && (
          <form onSubmit={handleVerifyOTP} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Verification Code
              </Label>
              <Input
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                disabled={loading}
                className="h-14 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 text-center text-2xl font-mono tracking-widest"
                data-testid="fp-otp-input"
              />
              <p className="text-xs text-muted-foreground">
                Code expires in 10 minutes
              </p>
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading || otp.length !== 6}
              data-testid="fp-verify-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                  Verifying…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Verify Code
                </span>
              )}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendTimer > 0 || loading}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
              </button>
            </div>
          </form>
        )}

        {fpStep === FP_NEWPWD && (
          <form onSubmit={handleReset} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                New Password
              </Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  placeholder="Min 8 characters"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  onFocus={() => setPwdFocused(true)}
                  onBlur={() => setPwdFocused(false)}
                  required
                  disabled={loading}
                  className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 pr-11"
                  data-testid="fp-newpwd-input"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPwd ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {(pwdFocused || newPwd) && <PwdStrengthBar password={newPwd} />}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  type={showCPwd ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  disabled={loading}
                  className={`h-11 bg-secondary border-0 focus:ring-2 pr-11 ${
                    confirmPwd && newPwd !== confirmPwd
                      ? "ring-2 ring-red-500/50 focus:ring-red-500/50"
                      : confirmPwd && newPwd === confirmPwd
                        ? "ring-2 ring-green-500/50 focus:ring-green-500/50"
                        : "focus:ring-primary/30"
                  }`}
                  data-testid="fp-confirmpwd-input"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowCPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCPwd ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPwd && newPwd !== confirmPwd && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
              {confirmPwd && newPwd === confirmPwd && (
                <p className="text-xs text-green-500">Passwords match ✓</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-heading uppercase tracking-wider"
              disabled={loading || !isPasswordValid || newPwd !== confirmPwd}
              data-testid="fp-reset-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                  Resetting…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> Reset Password
                </span>
              )}
            </Button>
          </form>
        )}

        {fpStep === FP_DONE && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-9 w-9 text-green-500" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Your password has been reset. You can now sign in with your new
              password.
            </p>
            <Button
              onClick={onBack}
              className="w-full h-11 font-heading uppercase tracking-wider mt-2"
              data-testid="fp-back-to-login"
            >
              <span className="flex items-center gap-2">
                Back to Sign In <ArrowRight className="h-4 w-4" />
              </span>
            </Button>
          </div>
        )}

        {fpStep !== FP_DONE && (
          <div className="mt-6 pt-5 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{" "}
              <button
                onClick={onBack}
                className="text-primary font-medium hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Login page
// ---------------------------------------------------------------------------
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [signupPrevented, setSignupPrevented] = useState(false);

  useEffect(() => {
    api
      .get("/auth/signup-status")
      .then((res) => {
        if (res.data.preventSignup) {
          setSignupPrevented(true);
        }
      })
      .catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Branding Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-card border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
        <div className="absolute top-1/4 -left-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-black font-heading text-primary tracking-wider">
                Optimus Gym
              </h1>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">
                GYM CRM
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-black font-heading uppercase leading-tight">
              Manage Your
              <br />
              <span className="text-primary">Fitness Empire</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-md">
              The complete gym management platform. Members, memberships,
              payments, attendance — all in one place.
            </p>
            <div className="flex items-center gap-8 pt-4">
              <div>
                <p className="text-3xl font-black font-heading">500+</p>
                <p className="text-xs text-muted-foreground font-mono uppercase">
                  Active Gyms
                </p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div>
                <p className="text-3xl font-black font-heading">50K+</p>
                <p className="text-xs text-muted-foreground font-mono uppercase">
                  Members
                </p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div>
                <p className="text-3xl font-black font-heading">99.9%</p>
                <p className="text-xs text-muted-foreground font-mono uppercase">
                  Uptime
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            © 2026 Optimus Gym CRM. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center p-2 md:p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-xl font-black font-heading text-primary tracking-wider">
              OPTIMUS GYM
            </h1>
          </div>

          {showForgot ? (
            <ForgotPassword onBack={() => setShowForgot(false)} />
          ) : (
            <Card className="bg-card border-border shadow-xl">
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-heading uppercase tracking-wide">
                  Sign In
                </CardTitle>
                <CardDescription>
                  Enter your credentials to access your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      type="email"
                      placeholder="you@gym.com"
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      required
                      disabled={loading}
                      className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        Password
                      </Label>
                    </div>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={form.password}
                        onChange={(e) =>
                          setForm({ ...form, password: e.target.value })
                        }
                        required
                        disabled={loading}
                        className="h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        type="button"
                        onClick={() => setShowForgot(true)}
                        disabled={loading}
                        className="text-xs text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="forgot-password-link"
                      >
                        Forgot?
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 font-heading uppercase tracking-wider btn-glow"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                        Signing In...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>
                {!signupPrevented && (
                  <div
                    className={`mt-6 pt-6 border-t border-border text-center`}
                  >
                    <p className="text-sm text-muted-foreground">
                      Don't have an account?{" "}
                      <Link
                        to="/signup"
                        className="text-primary font-medium hover:underline"
                      >
                        Create one
                      </Link>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6 lg:hidden">
            © 2026 Optimus Gym CRM
          </p>
        </div>
      </div>
    </div>
  );
}
